import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { productsAPI, brandsAPI, equipmentTypesAPI, manufacturersAPI, partCategoriesAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Upload, X } from 'lucide-react';
import SearchableSelect from './SearchableSelect';

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [categories, setCategories] = useState({
    typeOfEquipment: [],
    brand: [],
    manufacturer: [],
    partCategory: []
  });
  const [showAddCategory, setShowAddCategory] = useState({
    typeOfEquipment: false,
    brand: false,
    manufacturer: false,
    partCategory: false
  });
  const [newCategoryValue, setNewCategoryValue] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch
  } = useForm({
    defaultValues: {
      typeOfEquipment: '',
      brand: '',
      model: '',
      partsName: '',
      partsNumber: '',
      manufacturer: '',
      partCategory: '',
      description: ''
    }
  });

  // Watch form values for validation
  const watchedValues = watch();

  useEffect(() => {
    const initializeData = async () => {
      try {
        // First fetch categories
        await fetchCategories();
        
        // Then fetch product data if editing
        if (isEdit) {
          await fetchProduct();
        }
      } catch (error) {
        console.error('Failed to initialize form data:', error);
        toast.error('Failed to load form data');
      }
    };

    initializeData();
  }, [id, isEdit]);

  // Debug effect to monitor form values
  useEffect(() => {
    if (isEdit && !loading) {
      console.log('Form values updated:', {
        typeOfEquipment: getValues('typeOfEquipment'),
        brand: getValues('brand'),
        model: getValues('model'),
        partsName: getValues('partsName'),
        partsNumber: getValues('partsNumber'),
        manufacturer: getValues('manufacturer'),
        partCategory: getValues('partCategory'),
        description: getValues('description')
      });
    }
  }, [isEdit, categories, loading]);

  const fetchCategories = async () => {
    try {
      console.log('Fetching categories from new APIs...');
      
      // Fetch all categories in parallel using new APIs
      const [brandsResponse, equipmentTypesResponse, manufacturersResponse, partCategoriesResponse] = await Promise.all([
        brandsAPI.getAllSimple(),
        equipmentTypesAPI.getAllSimple(),
        manufacturersAPI.getAllSimple(),
        partCategoriesAPI.getAllSimple()
      ]);
      
      console.log('API responses:', {
        brands: brandsResponse.data,
        equipmentTypes: equipmentTypesResponse.data,
        manufacturers: manufacturersResponse.data,
        partCategories: partCategoriesResponse.data
      });
      
      // Extract data from responses
      const brands = brandsResponse.data?.data?.brands || [];
      const equipmentTypes = equipmentTypesResponse.data?.data?.equipmentTypes || [];
      const manufacturers = manufacturersResponse.data?.data?.manufacturers || [];
      const partCategories = partCategoriesResponse.data?.data?.partCategories || [];
      
      setCategories({
        typeOfEquipment: equipmentTypes,
        brand: brands,
        manufacturer: manufacturers,
        partCategory: partCategories
      });
      
      console.log('Categories loaded successfully:', {
        typeOfEquipment: equipmentTypes,
        brand: brands,
        manufacturer: manufacturers,
        partCategory: partCategories
      });
      
    } catch (error) {
      console.error('Failed to fetch categories from new APIs:', error);
      toast.error('Failed to load categories');
      // Set empty arrays as fallback
      setCategories({
        typeOfEquipment: [],
        brand: [],
        manufacturer: [],
        partCategory: []
      });
    }
  };

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await productsAPI.getByIdAdmin(id);
      const product = response.data.data.product;
      
      console.log('Fetched product data:', product);
      
      // Set form values explicitly for each field
      const fieldsToSet = {
        typeOfEquipment: product.typeOfEquipment?.name || product.typeOfEquipment || '',
        brand: product.brand?.name || product.brand || '',
        model: product.model || '',
        partsName: product.partsName || '',
        partsNumber: product.partsNumber || '',
        manufacturer: product.manufacturer?.name || product.manufacturer || '',
        partCategory: product.partCategory?.name || product.partCategory || '',
        description: product.description || ''
      };

      // Set each field value
      Object.entries(fieldsToSet).forEach(([field, value]) => {
        setValue(field, value);
        console.log(`Set ${field}:`, value);
      });
      
      // Set existing images - combine main image and additional images
      const allImages = [];
      
      // Add main image if it exists
      if (product.image) {
        allImages.push(product.image);
      }
      
      // Add additional images if they exist
      if (product.additionalImages && Array.isArray(product.additionalImages)) {
        allImages.push(...product.additionalImages);
      }
      
      // Set all images (main + additional)
      if (allImages.length > 0) {
        setExistingImages(allImages);
        console.log('Set existing images:', allImages);
      }
      
      // Verify values were set correctly
      setTimeout(() => {
        const currentValues = getValues();
        console.log('Final form values after setting:', currentValues);
      }, 100);
      
      // Show success message with loaded data summary
      const loadedFields = Object.entries(fieldsToSet).filter(([_, value]) => value).length;
      toast.success(`Product data loaded successfully (${loadedFields} fields populated)`);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Failed to load product');
      navigate('/admin/products');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Limit to maximum 3 images total
      const maxImages = 3;
      const currentTotal = selectedFiles.length + existingImages.length;
      const availableSlots = maxImages - currentTotal;
      
      if (availableSlots <= 0) {
        toast.error(`Maximum ${maxImages} images allowed`);
        return;
      }
      
      // Take only the number of files that fit within the limit
      const filesToAdd = files.slice(0, availableSlots);
      const newFiles = [...selectedFiles, ...filesToAdd];
      setSelectedFiles(newFiles);
      
      // Create previews for new images
      const newPreviews = [];
      filesToAdd.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          newPreviews.push(e.target.result);
          if (newPreviews.length === filesToAdd.length) {
            setImagePreviews(prev => [...prev, ...newPreviews]);
          }
        };
        reader.readAsDataURL(file);
      });
      
      if (files.length > availableSlots) {
        toast.warning(`Only ${availableSlots} images added. Maximum ${maxImages} images allowed.`);
      }
    }
  };

  const removeImage = (index, isExisting = false) => {
    if (isExisting) {
      setExistingImages(prev => prev.filter((_, i) => i !== index));
    } else {
      setImagePreviews(prev => prev.filter((_, i) => i !== index));
      setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const removeAllImages = () => {
    setImagePreviews([]);
    setExistingImages([]);
    setSelectedFiles([]);
    // Reset file input
    const fileInput = document.getElementById('images');
    if (fileInput) fileInput.value = '';
  };

  const handleAddCategory = async (field) => {
    if (newCategoryValue.trim()) {
      try {
        setAddingCategory(true);
        
        // Map field names to API functions
        const apiMapping = {
          typeOfEquipment: equipmentTypesAPI.create,
          brand: brandsAPI.create,
          manufacturer: manufacturersAPI.create,
          partCategory: partCategoriesAPI.create
        };
        
        const apiFunction = apiMapping[field];
        
        // Add to backend using new API
        await apiFunction({ name: newCategoryValue.trim() });
        
        // Add to local state
        setCategories(prev => ({
          ...prev,
          [field]: [...(prev[field] || []), newCategoryValue.trim()]
        }));
        
        // Update form value
        setValue(field, newCategoryValue.trim());
        
        setNewCategoryValue('');
        setShowAddCategory(prev => ({
          ...prev,
          [field]: false
        }));
        
        toast.success(`${field} added successfully`);
        
      } catch (error) {
        console.error('Failed to add category:', error);
        const errorMessage = error.response?.data?.message || 'Failed to add category';
        toast.error(errorMessage);
      } finally {
        setAddingCategory(false);
      }
    }
  };

  const toggleAddCategory = (field) => {
    setShowAddCategory(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
    setNewCategoryValue('');
  };

  // Validate required fields (NEW REQUIREMENTS)
  const validateRequiredFields = (data) => {
    const requiredFields = {
      manufacturer: 'Manufacturer',
      description: 'Description',
      partCategory: 'Part Category',
      partsNumber: 'Parts Number',
      partsName: 'Parts Name'
    };

    const missingFields = [];
    
    Object.entries(requiredFields).forEach(([field, label]) => {
      if (!data[field] || data[field].trim() === '') {
        missingFields.push(label);
      }
    });

    return missingFields;
  };

  // Check optional fields and show warning (NEW REQUIREMENTS)
  const checkOptionalFields = (data) => {
    const optionalFields = {
      typeOfEquipment: 'Type of Equipment',
      brand: 'Brand',
      model: 'Model'
    };

    const missingOptionalFields = [];
    
    Object.entries(optionalFields).forEach(([field, label]) => {
      if (!data[field] || data[field].trim() === '') {
        missingOptionalFields.push(label);
      }
    });

    return missingOptionalFields;
  };

  const onSubmit = async (data) => {
    try {
      // Validate required fields first
      const missingRequired = validateRequiredFields(data);
      
      if (missingRequired.length > 0) {
        toast.error(`Please fill in the required fields: ${missingRequired.join(', ')}`);
        return;
      }

      // Check optional fields and show warning
      const missingOptional = checkOptionalFields(data);
      
      if (missingOptional.length > 0) {
        const shouldProceed = window.confirm(
          `The following fields are empty: ${missingOptional.join(', ')}\n\n` +
          'Do you want to continue without these fields?'
        );
        
        if (!shouldProceed) {
          return;
        }
      }

      setLoading(true);
      
      const formData = new FormData();
      
      // Add all form data
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined) {
          formData.append(key, data[key]);
        }
      });

      // Add images if selected (up to 3 images)
      if (selectedFiles.length > 0) {
        // Send up to 3 images with specific names
        selectedFiles.slice(0, 3).forEach((file, index) => {
          if (index === 0) {
            formData.append('image', file); // Primary image
          } else {
            formData.append(`additionalImage${index - 1}`, file); // Additional images
          }
        });
      }

      // Add existing images info if editing
      if (isEdit && existingImages.length > 0) {
        formData.append('existingImages', JSON.stringify(existingImages));
      }

      if (isEdit) {
        await productsAPI.update(id, formData);
        toast.success('Product updated successfully');
      } else {
        await productsAPI.create(formData);
        toast.success('Product created successfully');
      }
      
      navigate('/admin/products');
    } catch (error) {
      console.error('Failed to save product:', error);
      const message = error.response?.data?.message || 'Failed to save product';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/products')}
            className="p-2 text-secondary/60 hover:text-secondary hover:bg-primary/10 rounded-xl transition-colors duration-300"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-secondary font-wix">
              {isEdit ? 'Edit Product' : 'Add New Product'}
            </h1>
            <p className="text-secondary/70 font-wix">
              {isEdit ? 'Update product information' : 'Create a new product for your catalog'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Field Requirements Info */}
        <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-primary mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-primary mb-1 font-wix">Field Requirements</h3>
              <div className="text-sm text-secondary font-wix">
                <p className="mb-1"><span className="font-medium text-red-600">* Required:</span> Manufacturer, Description, Part Category, Parts Number, Parts Name</p>
                <p><span className="font-medium text-secondary/70">Optional:</span> Type of Equipment, Brand, Model</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Images Upload */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Product Images
                    <span className="text-gray-400 ml-1">(Optional)</span>
                  </label>
                  {(imagePreviews.length > 0 || existingImages.length > 0) && (
                    <button
                      type="button"
                      onClick={removeAllImages}
                      className="text-sm text-red-600 hover:text-red-800 font-medium"
                    >
                      Remove All
                    </button>
                  )}
                </div>
                
                <div className="space-y-4">
                  {/* Existing Images */}
                  {existingImages.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Existing Images:</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {existingImages.map((image, index) => (
                          <div key={`existing-${index}`} className="relative">
                            <img
                              src={image}
                              alt={`Product existing ${index + 1}`}
                              className="h-24 w-full object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index, true)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors duration-300"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New Image Previews */}
                  {imagePreviews.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">New Images:</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {imagePreviews.map((preview, index) => (
                          <div key={`preview-${index}`} className="relative">
                            <img
                              src={preview}
                              alt={`Product preview ${index + 1}`}
                              className="h-24 w-full object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index, false)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors duration-300"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* File Input */}
                  <div className="flex items-center justify-center w-full">
                    <label
                      htmlFor="images"
                      className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors duration-300"
                    >
                      <div className="flex flex-col items-center justify-center pt-4 pb-4">
                        <Upload className="w-8 h-8 mb-2 text-gray-400" />
                        <p className="mb-1 text-sm text-gray-600">
                          <span className="font-semibold">Click to upload</span> up to 3 images
                        </p>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB each (Max 3 images)</p>
                      </div>
                      <input
                        id="images"
                        type="file"
                        className="hidden"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                      />
                    </label>
                  </div>

                  {/* Images Count */}
                  {(imagePreviews.length > 0 || existingImages.length > 0) && (
                    <div className="text-center">
                      <p className="text-sm text-gray-500">
                        Total images: {imagePreviews.length + existingImages.length}
                        {imagePreviews.length > 0 && ` (${imagePreviews.length} new)`}
                        {existingImages.length > 0 && ` (${existingImages.length} existing)`}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Basic Information */}
              <div className="space-y-6">
                <div className="border-b border-gray-200 pb-2">
                  <h3 className="text-xl font-semibold text-secondary font-wix">Basic Information</h3>
                  <p className="text-sm text-secondary/70 mt-1 font-wix">Equipment and brand details</p>
                </div>
                
                <div>
                  <SearchableSelect
                    label={
                      <span>
                        Type of Equipment
                        <span className="text-gray-400 ml-1 font-normal">(Optional)</span>
                      </span>
                    }
                    value={watch('typeOfEquipment') || ''}
                    onChange={(value) => setValue('typeOfEquipment', value)}
                    options={categories.typeOfEquipment.map(type => typeof type === 'object' ? type.name : type)}
                    placeholder="Search for Equipment Type..."
                    emptyLabel={`Select Equipment Type (${categories.typeOfEquipment.length} available)`}
                  />
                  
                  {/* Add New Category Section */}
                  <div className="mt-3">
                    {!showAddCategory.typeOfEquipment ? (
                      <button
                        type="button"
                        onClick={() => toggleAddCategory('typeOfEquipment')}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors duration-300 font-wix"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add New Equipment Type
                      </button>
                    ) : (
                      <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-secondary font-wix">Add New Equipment Type</h4>
                          <button
                            type="button"
                            onClick={() => toggleAddCategory('typeOfEquipment')}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newCategoryValue}
                            onChange={(e) => setNewCategoryValue(e.target.value)}
                            placeholder="Enter new equipment type..."
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                            onKeyPress={(e) => e.key === 'Enter' && handleAddCategory('typeOfEquipment')}
                          />
                          <button
                            type="button"
                            onClick={() => handleAddCategory('typeOfEquipment')}
                            disabled={addingCategory}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed font-wix"
                          >
                            {addingCategory ? (
                              <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Adding...
                              </span>
                            ) : (
                              'Add'
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                </div>

                <div>
                  <SearchableSelect
                    label={
                      <span>
                        Brand
                        <span className="text-gray-400 ml-1 font-normal">(Optional)</span>
                      </span>
                    }
                    value={watch('brand') || ''}
                    onChange={(value) => setValue('brand', value)}
                    options={categories.brand.map(brand => typeof brand === 'object' ? brand.name : brand)}
                    placeholder="Search for Brand..."
                    emptyLabel={`Select Brand (${categories.brand.length} available)`}
                  />
                  
                  {/* Add New Category Section */}
                  <div className="mt-3">
                    {!showAddCategory.brand ? (
                      <button
                        type="button"
                        onClick={() => toggleAddCategory('brand')}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors duration-300 font-wix"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add New Brand
                      </button>
                    ) : (
                      <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-secondary font-wix">Add New Brand</h4>
                          <button
                            type="button"
                            onClick={() => toggleAddCategory('brand')}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newCategoryValue}
                            onChange={(e) => setNewCategoryValue(e.target.value)}
                            placeholder="Enter new brand..."
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                            onKeyPress={(e) => e.key === 'Enter' && handleAddCategory('brand')}
                          />
                          <button
                            type="button"
                            onClick={() => handleAddCategory('brand')}
                            disabled={addingCategory}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed font-wix"
                          >
                            {addingCategory ? (
                              <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Adding...
                              </span>
                            ) : (
                              'Add'
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-2 font-wix">
                    Model
                    <span className="text-gray-400 ml-1">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    {...register('model')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                    placeholder="e.g., FH16, R450, Actros"
                  />
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Parts Information */}
              <div className="space-y-6">
                <div className="border-b border-gray-200 pb-2">
                  <h3 className="text-xl font-semibold text-secondary font-wix">Parts Information</h3>
                  <p className="text-sm text-secondary/70 mt-1 font-wix">Part details and specifications</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2 font-wix">
                    Parts Name *
                  </label>
                  <input
                    type="text"
                    {...register('partsName', { required: 'Parts name is required' })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                    placeholder="e.g., Brake Pad, Air Filter, Oil Filter"
                  />
                  {errors.partsName && (
                    <p className="mt-2 text-sm text-red-600">{errors.partsName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-2 font-wix">
                    Parts Number *
                  </label>
                  <input
                    type="text"
                    {...register('partsNumber', { required: 'Parts number is required' })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                    placeholder="e.g., BP001, AF002, OF003"
                  />
                  {errors.partsNumber && (
                    <p className="mt-2 text-sm text-red-600">{errors.partsNumber.message}</p>
                  )}
                </div>

                <div>
                  <SearchableSelect
                    label={
                      <span>
                        Part Category
                        <span className="text-red-500 ml-1">*</span>
                      </span>
                    }
                    value={watch('partCategory') || ''}
                    onChange={(value) => setValue('partCategory', value, { shouldValidate: true })}
                    options={categories.partCategory.map(category => typeof category === 'object' ? category.name : category)}
                    placeholder="Search for Part Category..."
                    emptyLabel={`Select Part Category (${categories.partCategory.length} available)`}
                  />
                  
                  {/* Add New Category Section */}
                  <div className="mt-3">
                    {!showAddCategory.partCategory ? (
                      <button
                        type="button"
                        onClick={() => toggleAddCategory('partCategory')}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors duration-300 font-wix"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add New Part Category
                      </button>
                    ) : (
                      <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-secondary font-wix">Add New Part Category</h4>
                          <button
                            type="button"
                            onClick={() => toggleAddCategory('partCategory')}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newCategoryValue}
                            onChange={(e) => setNewCategoryValue(e.target.value)}
                            placeholder="Enter new part category..."
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                            onKeyPress={(e) => e.key === 'Enter' && handleAddCategory('partCategory')}
                          />
                          <button
                            type="button"
                            onClick={() => handleAddCategory('partCategory')}
                            disabled={addingCategory}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed font-wix"
                          >
                            {addingCategory ? (
                              <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Adding...
                              </span>
                            ) : (
                              'Add'
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {errors.partCategory && (
                    <p className="mt-2 text-sm text-red-600">{errors.partCategory.message}</p>
                  )}
                </div>

                <div>
                  <SearchableSelect
                    label={
                      <span>
                        Manufacturer
                        <span className="text-red-500 ml-1">*</span>
                      </span>
                    }
                    value={watch('manufacturer') || ''}
                    onChange={(value) => setValue('manufacturer', value, { shouldValidate: true })}
                    options={categories.manufacturer.map(manufacturer => typeof manufacturer === 'object' ? manufacturer.name : manufacturer)}
                    placeholder="Search for Manufacturer..."
                    emptyLabel={`Select Manufacturer (${categories.manufacturer.length} available)`}
                  />
                  
                  {/* Add New Category Section */}
                  <div className="mt-3">
                    {!showAddCategory.manufacturer ? (
                      <button
                        type="button"
                        onClick={() => toggleAddCategory('manufacturer')}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors duration-300 font-wix"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add New Manufacturer
                      </button>
                    ) : (
                      <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-secondary font-wix">Add New Manufacturer</h4>
                          <button
                            type="button"
                            onClick={() => toggleAddCategory('manufacturer')}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newCategoryValue}
                            onChange={(e) => setNewCategoryValue(e.target.value)}
                            placeholder="Enter new manufacturer..."
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                            onKeyPress={(e) => e.key === 'Enter' && handleAddCategory('manufacturer')}
                          />
                          <button
                            type="button"
                            onClick={() => handleAddCategory('manufacturer')}
                            disabled={addingCategory}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed font-wix"
                          >
                            {addingCategory ? (
                              <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Adding...
                              </span>
                            ) : (
                              'Add'
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {errors.manufacturer && (
                    <p className="mt-2 text-sm text-red-600">{errors.manufacturer.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-2 font-wix">
                    Description *
                  </label>
                  <textarea
                    {...register('description', { required: 'Description is required' })}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 resize-none"
                    placeholder="Additional details about the part..."
                  />
                  {errors.description && (
                    <p className="mt-2 text-sm text-red-600">{errors.description.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-8 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/admin/products')}
              className="px-6 py-3 border border-gray-300 rounded-xl text-sm font-medium text-secondary bg-white hover:bg-gray-50 transition-all duration-300 font-wix"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 transition-all duration-300 disabled:cursor-not-allowed font-wix"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                isEdit ? 'Update Product' : 'Create Product'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;